import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState, type RefObject } from "react";

import type {
  DatabaseSearchPage,
  DatabaseSearchResult,
  SearchSortColumn,
} from "@/models/DatabaseSearch";
import {
  MUTED_TEXT,
  NAVY,
  PICKED_BG,
  WARNING_BG,
  WARNING_BORDER,
  WARNING_TEXT,
  visuallyHidden,
} from "./styles";
import { MAX_SELECTIONS } from "@/utils/databaseSearchSession";
import WebsiteLink, { safeHref } from "./WebsiteLink";

export const EMPTY_RESULTS_NOTE =
  "No opportunities match these filters. Try removing a filter, adding locations, or setting Round timing to Any time.";
const LIMIT_TOOLTIP_ID = "search-results-limit-tip";
export const LIMIT_TOOLTIP = `You've selected the maximum of ${MAX_SELECTIONS}. Untick one to choose this instead.`;
export const LIMIT_NOTE = `You've selected the maximum of ${MAX_SELECTIONS}. Untick one to choose a different opportunity, or use View & export to download them.`;

export const COLUMNS: { key: SearchSortColumn | "tags"; label: string }[] = [
  { key: "funder", label: "Funder" },
  { key: "opportunity", label: "Opportunity" },
  { key: "link", label: "Link" },
  { key: "application_type", label: "Application type" },
  { key: "location", label: "Location" },
  { key: "timing", label: "Timing" },
  // Not sortable: an opportunity's tags have no order to sort by.
  { key: "tags", label: "Tags" },
];

const LOCATION_COLOURS: Record<DatabaseSearchResult["location"], string> = {
  International: "#5E35B1",
  National: NAVY,
  Local: "#2E7D32",
};

function TimingCell({ row }: { row: DatabaseSearchResult }) {
  return (
    <>
      <Box component="span">{row.timing}</Box>
      {row.closing_soon && (
        // Same chip as "Closing Soon" on the match results.
        <Chip
          label="Closing Soon"
          size="small"
          sx={{
            ml: 1,
            height: "20px",
            fontSize: "11px",
            bgcolor: "warning.main",
            color: "white",
            fontWeight: 600,
          }}
        />
      )}
    </>
  );
}

function LinkCell({ row }: { row: DatabaseSearchResult }) {
  const href = safeHref(row.link);
  if (!href) {
    return (
      <Typography sx={{ fontSize: "14px", color: MUTED_TEXT }}>
        None provided
      </Typography>
    );
  }
  const link = <WebsiteLink href={href} opportunity={row.opportunity} />;
  // describeChild reads the tooltip as a description, keeping the link's name.
  return row.link_is_funder_website ? (
    <Tooltip title="Funder website (no opportunity link on file)" describeChild>
      {link}
    </Tooltip>
  ) : (
    link
  );
}

function LocationCell({ row }: { row: DatabaseSearchResult }) {
  const label = (
    <Box
      component="span"
      sx={{ color: LOCATION_COLOURS[row.location], fontWeight: 600 }}
    >
      {row.location}
    </Box>
  );
  if (row.location !== "Local") return label;
  return (
    <Box component="details">
      <Box component="summary" sx={{ cursor: "pointer" }}>
        {label}{" "}
        <Box component="span" sx={{ color: MUTED_TEXT }}>
          ({row.locations.length}{" "}
          {row.locations.length === 1 ? "area" : "areas"})
        </Box>
      </Box>
      <Typography sx={{ fontSize: "14px", color: MUTED_TEXT, mt: 0.5 }}>
        {row.locations.join(", ")}
      </Typography>
    </Box>
  );
}

function PickCell({
  row,
  picked,
  atLimit,
  onToggle,
}: {
  row: DatabaseSearchResult;
  picked: boolean;
  atLimit: boolean;
  onToggle: (row: DatabaseSearchResult) => void;
}) {
  // aria-disabled, not disabled, keeps the box focusable so keyboard users
  // reach the tooltip saying why it can't be ticked.
  const blocked = atLimit && !picked;
  const box = (
    <Checkbox
      size="small"
      checked={picked}
      onChange={() => {
        if (!blocked) onToggle(row);
      }}
      inputProps={{
        "aria-label": `Select ${row.opportunity} from ${row.funder}`,
        "aria-disabled": blocked || undefined,
        // The Tooltip describes the Checkbox's wrapper, not the input that
        // takes focus, so the input points at the reason itself.
        "aria-describedby": blocked ? LIMIT_TOOLTIP_ID : undefined,
      }}
      sx={{
        p: 0.5,
        color: NAVY,
        "&.Mui-checked": { color: NAVY },
        ...(blocked && { opacity: 0.4, cursor: "not-allowed" }),
      }}
    />
  );
  return blocked ? (
    <Tooltip title={LIMIT_TOOLTIP} describeChild>
      {box}
    </Tooltip>
  ) : (
    box
  );
}

interface SearchResultsProps {
  page: DatabaseSearchPage;
  sortBy: SearchSortColumn;
  sortDir: "asc" | "desc";
  stale: boolean;
  onSort: (column: SearchSortColumn) => void;
  onPage: (page: number) => void;
  /** Opportunity IDs ticked, on this page or any other. */
  pickedIds: Set<string>;
  onTogglePick: (row: DatabaseSearchResult) => void;
}

/**
 * How the table sits in its scroll box: whether it runs past the right edge,
 * whether it is wider than the box at all, and how wide it is.
 */
function useHorizontalOverflow(
  ref: RefObject<HTMLDivElement>,
  tableShown: boolean,
) {
  const [more, setMore] = useState(false);
  const [width, setWidth] = useState(0);
  const [overflowing, setOverflowing] = useState(false);
  // Space the box's own scroll bars take, so overlays can stop short of them.
  const [gutters, setGutters] = useState({ right: 0, bottom: 0 });
  useEffect(() => {
    const box = ref.current;
    if (!box) return;
    const update = () => {
      setMore(box.scrollLeft + box.clientWidth < box.scrollWidth - 1);
      setOverflowing(box.scrollWidth > box.clientWidth + 1);
      setWidth(box.scrollWidth);
      const right = box.offsetWidth - box.clientWidth;
      const bottom = box.offsetHeight - box.clientHeight;
      // Runs on every scroll event, so keep the old object when nothing moved.
      setGutters((old) =>
        old.right === right && old.bottom === bottom ? old : { right, bottom },
      );
    };
    update();
    box.addEventListener("scroll", update, { passive: true });
    // Watches the table too, since a new page of rows can change its width.
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(box);
    if (box.firstElementChild) observer?.observe(box.firstElementChild);
    return () => {
      box.removeEventListener("scroll", update);
      observer?.disconnect();
    };
    // Re-runs when the table appears, since the box isn't there while empty.
  }, [ref, tableShown]);
  return { more, overflowing, width, gutters };
}

// Always-visible scroll bars: macOS hides overlay scroll bars until you
// scroll, which hides the fact that there are more columns. Chrome ignores the
// ::-webkit-scrollbar styles when scrollbar-width is set, so the standard
// properties apply only where those styles aren't supported (Firefox).
const visibleScrollbarSx = {
  "&::-webkit-scrollbar": { height: 10, width: 10 },
  "&::-webkit-scrollbar-thumb": { bgcolor: "#b0b7c0", borderRadius: 5 },
  "&::-webkit-scrollbar-track": { bgcolor: "#f1f3f5" },
  "@supports not selector(::-webkit-scrollbar)": {
    scrollbarWidth: "thin",
    scrollbarColor: `${MUTED_TEXT} transparent`,
  },
} as const;

/**
 * A second sideways scroll bar above the table, kept in step with the one at
 * its foot, so the hidden columns can be reached without scrolling down.
 * Hidden from screen readers: the table's own box is the scrollable region.
 */
function TopScrollbar({
  box,
  width,
  gutter,
}: {
  box: RefObject<HTMLDivElement>;
  width: number;
  /** Width of the table box's vertical scroll bar. */
  gutter: number;
}) {
  const top = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const below = box.current;
    const above = top.current;
    if (!below || !above) return;
    // Setting the same scrollLeft fires no further scroll event, so the two
    // listeners settle rather than loop.
    const fromBelow = () => {
      above.scrollLeft = below.scrollLeft;
    };
    const fromAbove = () => {
      below.scrollLeft = above.scrollLeft;
    };
    below.addEventListener("scroll", fromBelow, { passive: true });
    above.addEventListener("scroll", fromAbove, { passive: true });
    fromBelow();
    return () => {
      below.removeEventListener("scroll", fromBelow);
      above.removeEventListener("scroll", fromAbove);
    };
  }, [box]);
  return (
    <Box
      ref={top}
      aria-hidden="true"
      // Browsers make scroll boxes focusable; this one is hidden from
      // screen readers, so it must stay out of the tab order too.
      tabIndex={-1}
      data-testid="search-results-top-scrollbar"
      sx={{
        overflowX: "auto",
        overflowY: "hidden",
        mb: 0.5,
        ...visibleScrollbarSx,
      }}
    >
      {/* This box has no vertical scroll bar, so it needs the table box's
        one added to reach the same furthest scroll position. */}
      <Box style={{ width: width + gutter, height: "1px" }} />
    </Box>
  );
}

function TagsCell({ tags }: { tags: string[] }) {
  if (tags.length <= 1) return <>{tags[0] ?? ""}</>;
  return (
    <Box component="details">
      <Box component="summary" sx={{ cursor: "pointer" }}>
        Multiple ({tags.length})
      </Box>
      <Typography sx={{ fontSize: "14px", color: MUTED_TEXT, mt: 0.5 }}>
        {tags.join(", ")}
      </Typography>
    </Box>
  );
}

export default function SearchResults({
  page,
  sortBy,
  sortDir,
  stale,
  onSort,
  onPage,
  pickedIds,
  onTogglePick,
}: SearchResultsProps) {
  const atLimit = pickedIds.size >= MAX_SELECTIONS;
  const scrollBox = useRef<HTMLDivElement>(null);
  const empty = page.total === 0;
  const {
    more: moreToTheRight,
    overflowing,
    width: tableWidth,
    gutters,
  } = useHorizontalOverflow(scrollBox, !empty);
  // The box keeps its scroll position across renders, so a new page, sort or
  // search would otherwise open part-way down. page.items is a new array for
  // each response, but stays the same when only the ticks change.
  useEffect(() => {
    const box = scrollBox.current;
    if (!box) return;
    box.scrollTop = 0;
    box.scrollLeft = 0;
  }, [page.items, page.page, sortBy, sortDir]);
  return (
    <Card
      data-testid="search-results"
      data-stale={stale}
      // Stale results keep full contrast; the border and banner mark them.
      sx={{
        outline: stale ? `2px solid ${WARNING_BORDER}` : "none",
        outlineOffset: -2,
      }}
    >
      <CardContent>
        {stale && (
          <Typography
            sx={{
              fontSize: "14px",
              color: WARNING_TEXT,
              bgcolor: WARNING_BG,
              borderRadius: 1,
              px: 1.5,
              py: 1,
              mb: 1.5,
            }}
          >
            These results are from your last search. Click Search to update
            them.
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography
            id="search-results-count"
            component="h2"
            sx={{ fontWeight: 500, fontSize: "18px", color: NAVY }}
          >
            {page.total}{" "}
            {page.total === 1 ? "opportunity matches" : "opportunities match"}
          </Typography>
          {!empty && (
            <Typography sx={{ fontSize: "14px", color: MUTED_TEXT }}>
              Select a column heading to sort. Select it again to reverse.
              Select View website to see programme info. Tick results you want
              to save and export.
              {moreToTheRight && (
                <>
                  {" "}
                  <Box
                    component="span"
                    sx={{ display: "block", fontWeight: 600, color: NAVY }}
                  >
                    Scroll sideways to see more columns{" "}
                    <span aria-hidden="true">→</span>
                  </Box>
                </>
              )}
            </Typography>
          )}
        </Box>
        {atLimit && (
          <Box id={LIMIT_TOOLTIP_ID} component="span" sx={visuallyHidden}>
            {LIMIT_TOOLTIP}
          </Box>
        )}
        {/* Always mounted so the note is announced when it appears. */}
        <Box role="status">
          {atLimit && (
            <Typography
              sx={{
                fontSize: "14px",
                color: WARNING_TEXT,
                bgcolor: WARNING_BG,
                border: `1px solid ${WARNING_BORDER}`,
                borderRadius: 1,
                px: 1.5,
                py: 1,
                mb: 1,
              }}
            >
              {LIMIT_NOTE}
            </Typography>
          )}
        </Box>
        {empty ? (
          <Typography sx={{ color: MUTED_TEXT, py: 2 }}>
            {EMPTY_RESULTS_NOTE}
          </Typography>
        ) : (
          <>
            {overflowing && (
              <TopScrollbar
                box={scrollBox}
                width={tableWidth}
                gutter={gutters.right}
              />
            )}
            <Box sx={{ position: "relative" }}>
              {/* Scrolls sideways instead of crushing the columns. The height cap
                keeps the sideways scroll bar on screen. */}
              {/* Focusable so keyboard users can scroll it with the arrow keys. */}
              <TableContainer
                ref={scrollBox}
                data-testid="search-results-scroll"
                role="region"
                aria-labelledby="search-results-count"
                tabIndex={0}
                sx={{
                  maxHeight: "75vh",
                  ...visibleScrollbarSx,
                  "&:focus-visible": {
                    outline: `2px solid ${NAVY}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  aria-labelledby="search-results-count"
                  // Narrow enough that two-word cells wrap rather than overflow.
                  sx={{ minWidth: 640 }}
                >
                  <TableHead>
                    <TableRow>
                      {/* No select-all: the cap and the rate limits stop bulk copying. */}
                      <TableCell sx={{ width: 36, pr: 0 }}>
                        <Box component="span" sx={visuallyHidden}>
                          Select
                        </Box>
                      </TableCell>
                      {COLUMNS.map(({ key, label }) => (
                        <TableCell
                          key={key}
                          sortDirection={sortBy === key ? sortDir : false}
                          sx={{ verticalAlign: "bottom" }}
                        >
                          {key === "tags" ? (
                            label
                          ) : (
                            <TableSortLabel
                              active={sortBy === key}
                              direction={sortBy === key ? sortDir : "asc"}
                              onClick={() => onSort(key)}
                              sx={{
                                "&.Mui-focusVisible": {
                                  outline: `2px solid ${NAVY}`,
                                  outlineOffset: 2,
                                  borderRadius: 0.5,
                                },
                              }}
                            >
                              {label}
                            </TableSortLabel>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {page.items.map((row) => (
                      <TableRow
                        key={row.opportunity_uuid}
                        hover
                        selected={pickedIds.has(row.opportunity_uuid)}
                        sx={{
                          verticalAlign: "top",
                          "&.Mui-selected, &.Mui-selected:hover": {
                            bgcolor: PICKED_BG,
                          },
                        }}
                      >
                        <TableCell sx={{ width: 36, pr: 0 }}>
                          <PickCell
                            row={row}
                            picked={pickedIds.has(row.opportunity_uuid)}
                            atLimit={atLimit}
                            onToggle={onTogglePick}
                          />
                        </TableCell>
                        <TableCell>{row.funder}</TableCell>
                        <TableCell>{row.opportunity}</TableCell>
                        <TableCell>
                          <LinkCell row={row} />
                        </TableCell>
                        <TableCell>{row.application_type}</TableCell>
                        <TableCell>
                          <LocationCell row={row} />
                        </TableCell>
                        <TableCell>
                          <TimingCell row={row} />
                        </TableCell>
                        <TableCell sx={{ color: MUTED_TEXT, minWidth: 110 }}>
                          <TagsCell tags={row.tags} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                aria-hidden="true"
                sx={{
                  position: "absolute",
                  top: 0,
                  // Stops at the scroll bars so the fade never covers them.
                  right: gutters.right,
                  bottom: gutters.bottom,
                  width: 40,
                  pointerEvents: "none",
                  background:
                    "linear-gradient(to right, rgba(255,255,255,0), #fff)",
                  opacity: moreToTheRight ? 1 : 0,
                  transition: "opacity 0.2s",
                }}
              />
            </Box>
          </>
        )}
        {page.total > page.page_size && (
          <TablePagination
            component="div"
            count={page.total}
            page={page.page - 1}
            rowsPerPage={page.page_size}
            rowsPerPageOptions={[]}
            onPageChange={(_, next) => onPage(next + 1)}
          />
        )}
      </CardContent>
    </Card>
  );
}
